// Utilities
import {gsap} from 'gsap';
import {disableBodyScroll, enableBodyScroll} from 'body-scroll-lock';
import WindowMetrics from '../../utilities/window-metrics';

// Components
import Backdrop from '../backdrop';
import SubMenu from './sub-menu';

const settings = {
    selectors: {
        container: 'header',
        hamburger: '.header__mobile__menu > button',
        nav: '.header__nav',
        navMenuContainer: '.header__nav > div',
        subMenus: 'ul.menu > li.menu-item-has-children',
        searchContainer: '.header__search',
        desktopSearchInput: 'input[type="text"]',
    },

    classNames: {
        sticky: 'header--sticky',
        open: 'header--open',
        closing: 'header--closing',
        scroll: 'header--scroll',
        searchHasInput: 'header__search--has-input',
    },

    cssProperties: {
        shrink: '--header-shrink',
    },

    // This was originally "extraLarge"
    finalMobileBreakpoint: WindowMetrics.breakpoints.small,
    desktopShrinkDistance: (
        // Tertiary nav shifts offscreen
        54 +

        // Main nav height reduces (130 - 100)
        30
    ),
};

// Our single header instance, created via Header.init()
let header = null;

export default class Header {
    constructor() {
        this.sticky = false;
        this.currentShrinkAmount = 0;
        this.menuOpen = false;
        this.openSubMenu = null;

        // Locate our DOM elements
        this.wpAdminBar = document.getElementById('wpadminbar');
        this.container = document.querySelector(settings.selectors.container);
        this.hamburger = this.container
            .querySelector(settings.selectors.hamburger);
        this.nav = this.container.querySelector(settings.selectors.nav);
        this.navMenuContainer = this.container
            .querySelector(settings.selectors.navMenuContainer);

        if (
            !this.container ||
            !this.hamburger ||
            !this.nav ||
            !this.navMenuContainer
        ) {
            throw new Error();
        }

        // Create backdrop
        this.backdrop = new Backdrop(
            this.container,
            (event) => {
                // Stop if we're not open
                if (!this.menuOpen) {
                    return;
                }

                // Close the menu
                this.closeMenu();
            },
        );

        // Setup hamburger
        let lastHamburgerClickTime = 0;
        this.hamburger.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            // Debounce double-click events
            const newHamburgerClickTime = (new Date()).getTime();
            if (newHamburgerClickTime - lastHamburgerClickTime < 100) {
                return;
            }

            lastHamburgerClickTime = newHamburgerClickTime;
            toggleMobileMenu.call(this, event);
        });

        this.hamburger.addEventListener(
            'keydown',
            (event) => {
                if (event.key == 'Enter') {
                    event.preventDefault();
                    event.stopPropagation();

                    toggleMobileMenu.call(this, event);
                }
            },
        );

        // Setup desktop search input; this keeps the input visible
        // while copy has been entered into it
        const searchContainer = this.container
            .querySelector(settings.selectors.searchContainer);
        if (searchContainer) {
            const desktopSearchInput = this.container
                .querySelector(settings.selectors.desktopSearchInput);
            if (desktopSearchInput) {
                let searchHasInput = false;
                desktopSearchInput.addEventListener('change', () => {
                    const value = desktopSearchInput.value;
                    const trimmedValue = value.trim();
                    if (value.length != trimmedValue.length) {
                        desktopSearchInput.value = trimmedValue;
                    }

                    if (trimmedValue.length > 0) {
                        if (!searchHasInput) {
                            searchHasInput = true;
                            searchContainer.classList
                                .add(settings.classNames.searchHasInput);
                        }
                    } else {
                        if (searchHasInput) {
                            searchHasInput = false;
                            searchContainer.classList
                                .remove(settings.classNames.searchHasInput);
                        }
                    }
                });
            }
        }

        // Setup submenus
        this.subMenus = [];
        const subMenuElements = this.container
            .querySelectorAll(settings.selectors.subMenus);
        const subMenuCallbacks = {
            onOpenStart: (subMenu) => {
                // If a submenu is open, close it
                if (this.openSubMenu) {
                    if (this.openSubMenu != subMenu) {
                        this.openSubMenu.close();
                    }
                }

                this.openSubMenu = subMenu;
            },

            onOpenEnd: (subMenu) => {
                // Adjust scrolling due to potential change in menu height
                if (this.menuOpen) {
                    adjustScrolling.call(this);
                }
            },

            onCloseStart: (subMenu) => {
                if (this.openSubMenu == subMenu) {
                    this.openSubMenu = null;
                }
            },

            onCloseEnd: (subMenu) => {
                // Adjust scrolling due to potential change in menu height
                if (this.menuOpen) {
                    adjustScrolling.call(this);
                }
            },
        };

        for (const subMenuElement of subMenuElements) {
            const subMenu = new SubMenu(
                subMenuElement,
                settings.finalMobileBreakpoint,
                subMenuCallbacks,
            );

            this.subMenus.push(subMenu);
        }

        // Check if we're in mobile or desktop mode
        this.isMobile = WindowMetrics.isMobile(
            WindowMetrics.currentBreakpoint,
            settings.finalMobileBreakpoint,
        );

        // Calculate WordPress admin bar height
        this.wpAdminBarHeight = this.wpAdminBar ?
            this.wpAdminBar.clientHeight :
            0;

        // Setup scroll event
        window.addEventListener('scroll', scrollCallback.bind(this));
        scrollCallback.call(this);

        // Setup resize event
        window.addEventListener('resize', resizeCallback.bind(this));
        resizeCallback.call(this);

        // Setup a mobile-to-desktop breakpoint change event
        WindowMetrics.addBeakpointChangeCallback(
            breakpointChangeCallback.bind(this),
            settings.finalMobileBreakpoint,
        );
    }

    static init() {
        // Stop if we've already initialized
        if (header) {
            return;
        }

        header = new Header();
    }

    /**
     * @return {Header}
     */
    static get current() {
        return header;
    }

    /**
     * Returns the shrunken header height; this is used for various
     * scrolling calculations when we want to avoid the header
     * obscuring any page content (e.g., anchor links)
     */
    static get height() {
        // First check if the global header has been initialized;
        // this won't be the case in modules that are compiled
        // separately from main.js
        if (header) {
            // Reference members in the initialized header
            if (header.isMobile) {
                // Mobile
                return header.container.clientHeight;
            }

            // Desktop - incorporate shrink calculations
            return (
                header.container.clientHeight -
                settings.desktopShrinkDistance
            );
        } else {
            // This is the flow for modules other than main.js
            const element = document.querySelector('header');
            if (!element) {
                return 0;
            }

            if (
                WindowMetrics.isMobile(
                    WindowMetrics.currentBreakpoint,
                    settings.finalMobileBreakpoint,
                )
            ) {
                // Mobile
                return element.clientHeight;
            } else {
                // Desktop - incorporate shrink calculations
                return (
                    element.clientHeight -
                    settings.desktopShrinkDistance
                );
            }
        }
    }

    openMenu(immediate = false) {
        if (this.menuOpen) {
            return;
        }

        this.menuOpen = true;

        // Stop any ongoing animation
        if (this.menuTimeline) {
            this.menuTimeline.progress(1);
        }

        // Lock scrolling
        disableBodyScroll(this.nav);

        // Show our backdrop
        this.backdrop.show(immediate);

        // Add our open classname
        this.container.classList.add(settings.classNames.open);

        if (immediate) {
            // Adjust whether scrolling in the nav is enabled or not
            adjustScrolling.call(this);
            return;
        }

        // Create timeline
        this.menuTimeline = gsap.timeline();

        this.menuTimeline.from(
            this.nav,
            {
                duration: 0.5,
                ease: 'power3.out',
                height: 0,
            },
            0,
        );

        this.menuTimeline.add(() => {
            // Remove explicit height from the nav menu
            this.nav.removeAttribute('style');

            // Adjust whether scrolling in the nav is enabled or not
            adjustScrolling.call(this);

            // Clear our timeline variable
            this.menuTimeline = null;
        });
    }

    closeMenu(immediate = false) {
        if (!this.menuOpen) {
            return;
        }

        this.menuOpen = false;

        // Stop any ongoing animation
        if (this.menuTimeline) {
            this.menuTimeline.progress(1);
        }

        // Unlock scrolling
        enableBodyScroll(this.nav);

        // Hide our backdrop
        this.backdrop.hide(immediate);

        // Add our closing classname & remove open classname
        this.container.classList.add(settings.classNames.closing);
        this.container.classList.remove(settings.classNames.open);

        if (immediate) {
            // Remove our closing & scroll classnames
            this.container.classList.remove(settings.classNames.closing);
            this.container.classList.remove(settings.classNames.scroll);

            // Reset nav styles
            this.nav.removeAttribute('style');
            return;
        }

        // Create timeline
        this.menuTimeline = gsap.timeline();

        this.menuTimeline.to(
            this.nav,
            {
                duration: 0.5,
                ease: 'power3.out',
                height: 0,
            },
            0,
        );

        this.menuTimeline.add(() => {
            // Remove our closing & scroll classnames
            this.container.classList.remove(settings.classNames.closing);
            this.container.classList.remove(settings.classNames.scroll);

            // Reset nav styles
            this.nav.removeAttribute('style');

            // Clear our timeline variable
            this.menuTimeline = null;
        });
    }
}

/*
    Internal functions
*/

/**
 * Toggles opening & closing the mobile menu
 * @this Header
 * @param {event} event
 */
function toggleMobileMenu(event) {
    event.preventDefault();
    event.stopPropagation();

    // Stop if we're not in mobile mode
    if (!this.isMobile) {
        return;
    }

    // Toggle nav menu visibility
    if (this.menuOpen) {
        this.closeMenu();
    } else {
        this.openMenu();
    }
}

/**
 * Enables or disables scrolling within the mobile menu
 * @this Header
 */
function adjustScrolling() {
    // Check if the height of the nav content can fit onscreen
    if (this.nav.clientHeight < this.navMenuContainer.clientHeight - 1) {
        // Enable scrolling
        this.container.classList.add(settings.classNames.scroll);
    } else {
        // Disable scrolling
        this.container.classList.remove(settings.classNames.scroll);
    }
}

/**
 * Scroll event callback
 * @this Header
 * @param {event} event
 */
function scrollCallback(event) {
    // Do nothing if the mobile menu is open
    if (this.menuOpen) {
        return;
    }

    // Determine our shrink & sticky states based on whether we're in
    // mobile or desktop as well as how far the window has scrolled
    let newShrinkAmount;
    let newStickyState;

    if (this.isMobile) {
        // Mobile - shrink amount is 0
        newShrinkAmount = 0;

        // Sticky state begins once we've scrolled past the top of
        // the page & the WordPress admin bar (if present)
        newStickyState = window.pageYOffset > this.wpAdminBarHeight;
    } else {
        // Desktop - shrink begins once we've scrolled past the top of
        // the page & the WordPress admin bar (if present), and sticky
        // state begins once we've scrolled past the predetermined
        // shrink distance
        if (
            window.pageYOffset <= this.wpAdminBarHeight
        ) {
            // We're not past the top of the page or WordPress admin bar
            newShrinkAmount = 0;
        } else {
            const distanceIntoShrink = window.pageYOffset -
                this.wpAdminBarHeight;

            if (distanceIntoShrink < settings.desktopShrinkDistance) {
                // We're in the range where we're shrinking
                newShrinkAmount = distanceIntoShrink /
                    settings.desktopShrinkDistance;

                // Trim the decimal resolution
                newShrinkAmount *= 1000;
                newShrinkAmount = Math.round(newShrinkAmount);
                newShrinkAmount /= 1000;
            } else {
                // We're fully shrunk
                newShrinkAmount = 1;
            }
        }

        newStickyState = newShrinkAmount >= 1;
    }

    if (newStickyState != this.sticky) {
        this.sticky = newStickyState;
        if (this.sticky) {
            this.container.classList.add(settings.classNames.sticky);
        } else {
            this.container.classList.remove(settings.classNames.sticky);
        }
    }

    if (newShrinkAmount != this.currentShrinkAmount) {
        // Update our current shrink amount
        this.currentShrinkAmount = newShrinkAmount;
        this.container.style.setProperty(
            settings.cssProperties.shrink,
            newShrinkAmount,
        );
    }
}

/**
 * Resize event callback
 * @this Header
 * @param {event} event
 */
function resizeCallback(event) {
    // Do nothing if the mobile menu isn't open
    if (!this.menuOpen) {
        return;
    }

    // Adjust whether scrolling in the nav is enabled or not
    adjustScrolling.call(this);
}

/**
 * Breakpoint change callback (WindowMetrics)
 * @this Header
 * @param {boolean} mobileDesktopTransition
 */
function breakpointChangeCallback(mobileDesktopTransition) {
    // Recalculate WordPress admin bar height at every breakpoint change
    this.wpAdminBarHeight = this.wpAdminBar ?
        this.wpAdminBar.clientHeight :
        0;

    // Remaining logic only needs to run when transitioning between
    // mobile and desktop
    if (!mobileDesktopTransition) {
        // Stop here
        return;
    }

    // Re-check if we're in mobile or desktop mode
    this.isMobile = WindowMetrics.isMobile(
        WindowMetrics.currentBreakpoint,
        settings.finalMobileBreakpoint,
    );

    // If a submenu is open, close it
    if (this.openSubMenu) {
        this.openSubMenu.close();
    }

    if (this.isMobile) {
        // Stop here
        return;
    }

    // We're in desktop
    if (this.menuOpen) {
        // The menu is open - close it immediately
        this.closeMenu(true);
    }
}
